import "./styles.scss";

import { MenuItem, Tooltip } from "@mui/material";
import { PortalType } from "contexts/portal.context";
import { SmallBody } from "helpers/TypographyComponents";
import type { CSSProperties } from "react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import StyledPopover from "../StyledPopover";
import TitleCover from "../TitleCover";

type TProps = {
    title: string;
    isbn?: string | null;
    titleCoverUrl?: string;
    nav?: VoidFunction;
    sx?: any;
    rowIndex?: number;
    isTermDeleted?: number | undefined;
    /**
     * Handle search query
     */
    handleQuery?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    showCover?: boolean;
    titleStyle?: CSSProperties;
    subtitle?: string;
    showPlaceholderLetter?: boolean;
};

const coverStyles = {
    width: "32px",
    fontSize: "12px",
    fontWeight: "400",
    borderRadius: "2px",
};

function ManagementTableTitleComponent({
    title,
    isbn,
    nav,
    sx,
    titleCoverUrl,
    rowIndex,
    isTermDeleted,
    handleQuery,
    showCover = true,
    titleStyle,
    subtitle,
    showPlaceholderLetter = true,
}: TProps) {
    const [t] = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const showPopover = Boolean(anchorEl);
    const closePopover = () => setAnchorEl(null);
    const portalType = useContext(PortalType);

    const handleOpenMenu = (e) => {
        e.stopPropagation();
        if (showPopover) return;
        setAnchorEl(e.currentTarget);
    };

    const handleSearchForTitle = () => {
        closePopover();
        // Work name (or title/issue name on press & AV) — never the ISBN.
        handleQuery!({ target: { value: title } } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <div className="management-general-table-title" style={sx}>
            {showCover && (
                <TitleCover
                    coverUrl={titleCoverUrl}
                    title={title}
                    style={{ ...coverStyles, ...titleStyle }}
                    id={`general-table-row-${rowIndex}-title-image`}
                    showPlaceholderLetter={showPlaceholderLetter}
                    color="work-color"
                />
            )}
            <div className="table-title-info">
                <Tooltip title={title} disableInteractive>
                    <span
                        className="title-info-titleName"
                        onClick={handleQuery && handleOpenMenu}
                        style={{
                            cursor: nav !== undefined ? "pointer" : "auto",
                            textDecoration: nav !== undefined ? "underline" : "unset",
                        }}
                        id={`general-table-row-${rowIndex}-title-name`}
                    >
                        {title ? (
                            <>
                                {title}
                                <StyledPopover open={showPopover} onClose={closePopover} anchorEl={anchorEl}>
                                    <MenuItem onClick={nav}>
                                        <SmallBody>{t(`Management.NavigateToTitle_${portalType}`)}</SmallBody>
                                    </MenuItem>
                                    <MenuItem onClick={handleSearchForTitle}>
                                        <SmallBody>{t(`Management.FilterTitle_${portalType}`)}</SmallBody>
                                    </MenuItem>
                                </StyledPopover>
                            </>
                        ) : (
                            <span
                                style={{
                                    color: "rgba(0, 0, 2, 0.5647058824)",
                                    fontSize: "11px",
                                    fontStyle: "italic",
                                }}
                            >
                                {t(portalType === "book" ? "Works.WorkDeleted" : "Titles.TitleRemoved")}
                            </span>
                        )}
                    </span>
                </Tooltip>
                {isbn ? (
                    <Tooltip title={isbn} disableInteractive>
                        <span className="title-info-date" id={`general-table-row-${rowIndex}-title-isbn`}>
                            {isbn}
                        </span>
                    </Tooltip>
                ) : null}
                {subtitle && <SmallBody color="grey">{subtitle}</SmallBody>}
                {isTermDeleted ? <span className="deleted-term">{t("AdvancePayment.TermDeleted")}</span> : <></>}
            </div>
        </div>
    );
}

export default ManagementTableTitleComponent;
